import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { handleFetchMore } from '@sketch/components'
import {
  GetNotificationsDocument,
  GetNotificationsQuery,
  GetNotificationsQueryVariables,
  NotificationInfoFragment,
  useGetNotificationsQuery,
} from '@sketch/gql-types'
import { useDebounceValue, useOnEvent } from '@sketch/utils'

import { notificationsQueryOptions } from '../../graphql'
import { useInboxSearchContext } from './InboxSearchContext'
import { buildSearchString } from './searchUtils'

// `userReceivedNewNotification` is dispatched by the bell-notifications
// subscription handler in cloud-frontend. Re-declaring the same field here so
// `@sketch/user`'s TS project sees it — declaration merging picks up the
// matching shape from cloud-frontend at runtime.
declare module '@sketch/utils' {
  export interface EventsMap {
    userReceivedNewNotification: NotificationInfoFragment
  }
}

const DAY_MS = 24 * 60 * 60 * 1000
const SEARCH_DEBOUNCE_MS = 300

const ENTRIES_PATH = ['me', 'notifications', 'entries']

// Inlined to avoid pulling @sketch/graphql-cache into @sketch/user just for
// pagination dedupe. Notifications always have an identifier.
const notificationDataId = (o: { __typename?: string; identifier?: string }) =>
  o.identifier ? `${o.__typename}:${o.identifier}` : undefined

type Bucket = 'today' | 'this-week' | 'older'

const BUCKET_LABELS: Record<Bucket, string> = {
  today: 'Today',
  'this-week': 'This Week',
  older: 'Older',
}

const getBucket = (createdAt: string): Bucket => {
  const diffDays = (Date.now() - new Date(createdAt).getTime()) / DAY_MS
  if (diffDays < 1) return 'today'
  if (diffDays < 7) return 'this-week'
  return 'older'
}

export type InboxItem = {
  id: string
  typename: string | undefined
  state: 'unread' | 'read'
  title: string | null
  subtitle: string | null
  createdAt: string
}

export type InboxGroup = { id: Bucket; label: string; items: InboxItem[] }

export const useInboxNotifications = () => {
  const { search, options, sort } = useInboxSearchContext()
  const debouncedSearch = useDebounceValue(search, SEARCH_DEBOUNCE_MS)
  const apolloClient = useApolloClient()

  const searchString = buildSearchString({ search: debouncedSearch, options })

  const queryVariables: GetNotificationsQueryVariables = useMemo(
    () => ({
      filter: {
        search: searchString || undefined,
        states: ['UNREAD', 'DONE', 'READ'],
      },
      sortOrder: sort === 'newest' ? 'CREATED_AT_DESC' : 'CREATED_AT_ASC',
    }),
    [searchString, sort]
  )

  const { data, fetchMore, loading, error } = useGetNotificationsQuery({
    ...notificationsQueryOptions,
    variables: queryVariables,
  })

  const entries = data?.me?.notifications?.entries ?? []
  const after = data?.me?.notifications?.meta?.after ?? null

  /**
   * Count of new notifications that have been silently prepended to the
   * cache since the consumer last reset. Consumers (e.g. InboxList) decide
   * when to surface this — typically only when the user has scrolled away
   * from the top — and call `resetNewNotificationsCount` to clear it.
   */
  const [newNotificationsCount, setNewNotificationsCount] = useState(0)

  const resetNewNotificationsCount = useCallback(() => {
    setNewNotificationsCount(0)
  }, [])

  // Reset the count when the active query changes (search or sort) — the
  // previous count refers to the previous list.
  useEffect(() => {
    setNewNotificationsCount(0)
  }, [queryVariables])

  /**
   * Silently prepend new notifications to the cached query when the
   * `notificationCreated` subscription fires, and bump the counter so
   * consumers can surface a "new notifications" indicator.
   *
   * Variables are read through a ref so the event handler stays referentially
   * stable across renders — otherwise `useOnEvent` would resubscribe every
   * render because its callback dep would change.
   */
  const queryVariablesRef = useRef(queryVariables)
  queryVariablesRef.current = queryVariables

  const handleNewNotification = useCallback(
    (newNotification: NotificationInfoFragment) => {
      // The "new notifications" badge surfaces newest-first (scroll up to the
      // top of the list), so the optimistic prepend only makes sense for
      // CREATED_AT_DESC. When sorting oldest-first the arrival belongs at the
      // end of the list — let the next refetch place it correctly.
      if (queryVariablesRef.current.sortOrder !== 'CREATED_AT_DESC') return

      let didPrepend = false

      apolloClient.cache.updateQuery<
        GetNotificationsQuery,
        GetNotificationsQueryVariables
      >(
        {
          query: GetNotificationsDocument,
          variables: queryVariablesRef.current,
        },
        existing => {
          if (!existing?.me?.notifications) return existing

          const alreadyPresent = existing.me.notifications.entries.some(
            entry => entry.identifier === newNotification.identifier
          )

          if (alreadyPresent) return existing

          didPrepend = true
          return {
            ...existing,
            me: {
              ...existing.me,
              notifications: {
                ...existing.me.notifications,
                entries: [
                  newNotification,
                  ...existing.me.notifications.entries,
                ],
              },
            },
          }
        }
      )

      if (didPrepend) {
        setNewNotificationsCount(count => count + 1)
      }
    },
    [apolloClient]
  )

  useOnEvent('userReceivedNewNotification', handleNewNotification)

  const buckets =
    sort === 'newest'
      ? (['today', 'this-week', 'older'] as const)
      : (['older', 'this-week', 'today'] as const)

  const groups: InboxGroup[] = buckets.flatMap(bucket => {
    const bucketEntries = entries.filter(
      entry => getBucket(entry.createdAt) === bucket
    )
    if (bucketEntries.length === 0) return []
    return [
      {
        id: bucket,
        label: BUCKET_LABELS[bucket],
        items: bucketEntries.map<InboxItem>(entry => ({
          id: entry.identifier,
          typename: entry.__typename,
          state: entry.state === 'UNREAD' ? 'unread' : 'read',
          title: entry.title,
          subtitle: entry.subtitle,
          createdAt: entry.createdAt,
        })),
      },
    ]
  })

  const loadMore = useMemo(
    () =>
      handleFetchMore(fetchMore, ENTRIES_PATH, {
        dataIdFromObject: notificationDataId,
        after,
      }),
    [fetchMore, after]
  )

  return {
    loading,
    error,
    groups,
    loadMore,
    after,
    newNotificationsCount,
    resetNewNotificationsCount,
  }
}
